
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import useStyles from '../../../assets/styles/DQProV2Styles';
import PoweredByDQ_Default from '../../../assets/images/PoweredByDQ_Default.png';
import PoweredByDQ_White from '../../../assets/images/PoweredByDQ_white.png';
import { useColorMode } from '../../../context/colorMode/colorMode.context';
import AccountMenu from '../../../components/AccountMenu/AccountMenu.comp';
// import SecurityOutlinedIcon from '@material-ui/icons/SecurityOutlined';
// import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
// import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
// import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
// import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import EmojiObjectsOutlinedIcon from '@material-ui/icons/EmojiObjectsOutlined';
import { useAuth } from '../../../context/auth';
import { LOCAL_STORAGE_KEYS, USER_ACCESS_LEVEL, PRODUCT_KEY, TOAST_TYPES } from '../../../utils/constants';
import { toast } from '../../../utils/utils';
import { canCreateProduct } from '../../../utils/productAccess';
import { canAuthorLearningProgram, isClientWorkspace, isDirectWorkspace } from '../../../utils/capabilities';
import { resolveSolutions, SOLUTION } from '../../../config/workspaceNav/solutions';
import UnlockCapabilityDialog from './components/UnlockCapabilityDialog.comp';
// Slide 4 — fixed for now, per Ian on 9 Sep. See the file for why it is static.
import {
    AUDIENCES,
    BUILD_CATEGORIES,
    IDEA_CARDS,
    NAMED_FRAMEWORKS,
} from './data/learnCreatorCatalog';

/*
 * The chosen audience, written into the "create program" box (Yufei-PW-08Oct-1).
 * The chip only coloured itself and handed the builder a tier code nothing
 * reads, so DQ AI asked for the age again. Written as the person's own words,
 * the age travels with the message, which the assistant does read. One line
 * at the end of the text: choosing another audience replaces it, and choosing
 * none removes it; anything typed above it is left alone.
 */
const AUDIENCE_LINE = /\s*Learners: [^\n]*$/;
const withAudience = (text, code) => {
    const base = String(text || '').replace(AUDIENCE_LINE, '');
    const a = AUDIENCES.find((x) => x.code === code);
    if (!a) return base;
    const ages = a.ages === 'all ages' ? 'all ages' : `ages ${a.ages}`;
    return `${base.trim() ? `${base.trimEnd()}\n` : ''}Learners: ${a.label} (${ages}).`;
};

const ALL_FRAMEWORKS = [
    'OECD: Future of Education and Skills 2030',
    'UNESCO: Global Media and Information Literacy Assessment Framework',
    'IEA: International Computer and Information Literacy Study 2018',
    'World Economic Forum: Advancing Digital Safety Framework',
    'UNESCO: A Global Framework of Reference on Digital Literacy Skills',
    'ISTE: ISTE Standards: For Students',
    'Education Testing Services: Framework for ICT Literacy',
    'European Certificate of Digital Literacy (ECDL): ICDL Base Syllabus',
    'DigComp 3.0',
    'UNESCO AI Competency Framework',
    'OECD AI Lit Framework',
];

/**
 * The opening prompt for a school's program request opened from the
 * Opportunity Pipeline (LA-PW-13Oct-02) — the same sentence the builder used
 * to put in its chat box.
 */
const requestPrompt = (pr) => {
    if (!pr || !pr.Theme) return '';
    let prompt = `Create a Child Online Safety program about "${pr.Theme}"`;
    if (pr.grade_level) prompt += ` for ${pr.grade_level}`;
    if (pr.NumSessions) prompt += `, ${pr.NumSessions} session${Number(pr.NumSessions) > 1 ? 's' : ''}`;
    if (pr.SessionDuration) prompt += ` of ${pr.SessionDuration} minutes each`;
    prompt += '.';
    if (pr.SubjectContextTopic) prompt += ` Connect it to ${pr.SubjectContextTopic}.`;
    if (pr.CulturalSensitivityDetails) prompt += ` Please consider these local sensitivities: ${pr.CulturalSensitivityDetails}.`;
    if (pr.AdditionalInfo) prompt += ` Other information from the school: ${String(pr.AdditionalInfo).trim().replace(/[.\s]+$/, '')}.`;
    return prompt;
};

const DQProV2StepZero = () => {
    const classes = useStyles();
    const { isDark } = useColorMode();
    const PoweredByDQ_Logo = isDark ? PoweredByDQ_White : PoweredByDQ_Default;
    // Set when the page was opened for a school's request; carried on to the builder.
    const programRequest = useLocation().state?.programRequest || null;
    const [inputValue, setInputValue] = useState(() => requestPrompt(programRequest));
    /*
     * The audience and category the person picked in the inspiration block.
     *
     * Audience is carried through to the builder as a TIER CODE, because the
     * assistant collects the same T1-T7 codes — handing it one means it does
     * not have to ask a question the person has already answered on screen.
     * Category only filters the ideas below; it is not sent anywhere.
     */
    const [audienceCode, setAudienceCode] = useState('');
    const [categoryId, setCategoryId] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [unlockOpen, setUnlockOpen] = useState(false);
    const [fwItems, setFwItems] = useState(
        () => ALL_FRAMEWORKS.slice(0, 3).map((text, i) => ({ id: i, text, exiting: false }))
    );
    const fwNextRef = useRef(3);

    // Open at the top, wherever the previous page was scrolled to (Yufei-PW-05Oct-1).
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const timer = setInterval(() => {
            setFwItems(prev => [{ ...prev[0], exiting: true }, prev[1], prev[2]]);
            setTimeout(() => {
                const nextText = ALL_FRAMEWORKS[fwNextRef.current % ALL_FRAMEWORKS.length];
                const nextId = fwNextRef.current++;
                setFwItems(prev => [prev[1], prev[2], { id: nextId, text: nextText, exiting: false }]);
            }, 400);
        }, 2000);
        return () => clearInterval(timer);
    }, []);
    const history = useHistory();
    const { authUser } = useAuth();

    /*
     * No redirects here, unlike the public page.
     *
     * That page bounces a signed-in user onward, which is right for it — it
     * is the anonymous front door. This page is reached ONLY by clicking
     * "Create a Program" in Solutions > LearnCreator, so arriving signed in
     * is the normal case and redirecting would skip the step entirely.
     */
    /*
     * A school or network with a partner is judged by what IT was given, never by
     * the plan: the plan on its session is the PARTNER's. Two halves, the same two
     * the server enforces on the builder:
     *
     *   · the org's own learning_program must carry `create` (LearnLinker does
     *     not) — granted by the partner, the network, or an approved unlock;
     *   · the person needs the create_learning_program capability (a network
     *     contact passes as a workspace owner).
     *
     * Without the org half, the click opens "Unlock a capability" — the school /
     * network view sends the request to the partner and shows LearnCreator as
     * Active or Requested. With the org half but not the person's, there is
     * nothing to unlock for the org, so it is a plain message instead.
     */
    const isPartnerClient = isClientWorkspace(authUser) && !isDirectWorkspace(authUser);
    const orgCanCreate = canCreateProduct(authUser, PRODUCT_KEY.LEARNING_PROGRAM);
    const clientLacksOrgAccess = isPartnerClient && !orgCanCreate;
    const clientLacksCapability = isPartnerClient && orgCanCreate && !canAuthorLearningProgram(authUser);

    /*
     * Everyone else (a partner, a direct workspace) owns its plan. LearnCreator is
     * granted by the P1 and LC solutions; a PbDQ plan that holds neither (P0) can
     * still reach this page by URL, so the click offers the unlock flow instead of
     * the builder. Non-PbDQ plans resolve to null and are never gated.
     */
    const solutions = resolveSolutions(authUser);
    const lacksLearnCreator = !isPartnerClient
        && solutions !== null
        && !solutions.includes(SOLUTION.P1)
        && !solutions.includes(SOLUTION.LC);

    const handleCreateProgram = async () => {
        if (clientLacksOrgAccess || lacksLearnCreator) {
            setUnlockOpen(true);
            return;
        }
        if (clientLacksCapability) {
            toast(TOAST_TYPES.WARN, 'LearnCreator', 'You do not have access to create programs.');
            return;
        }
        if (!inputValue.trim()) return;

        /*
         * A level-22 account (a school or network) may create here exactly when
         * it may enter the builder: the route guard's rule for level 22 on
         * /dq_pro/program/creation. P1B / LC schools and networks have
         * LearnCreator in their sidebar, which now opens this page (bug sheet
         * Ian-LC-30Sep-1); a blanket level-22 block made the button do nothing
         * for them. A P1A school is still stopped, as before.
         */
        if (authUser?.access_level === USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE
            && !(canCreateProduct(authUser, PRODUCT_KEY.LEARNING_PROGRAM) && canAuthorLearningProgram(authUser))) {
            // Direct-workspace level-22 accounts only; partner clients were handled above.
            toast(TOAST_TYPES.WARN, 'LearnCreator', 'You do not have access to create programs.');
            return;
        }
        const userMessage = inputValue.trim();
        localStorage.setItem(LOCAL_STORAGE_KEYS.dqpro_user_message, userMessage);

        /*
         * Straight to the builder on their OWN session.
         *
         * The public page mints a guest here, which is correct for an
         * anonymous visitor. Doing it for someone already signed in would
         * overwrite their session with a guest that holds no
         * learning_program entitlement, and the builder would 403.
         *
         * audienceCode rides along as the engine's tier code so the assistant
         * treats the audience step as answered rather than asking again.
         */
        history.push('/dq_pro/program/creation/preview', { userMessage, audienceCode, ...(programRequest ? { programRequest } : {}) });
    };

    // Did You Know stats — hidden for now (Approved Fixes); uncomment to restore.
    // const stats = [
    //     { value: '39%', label: 'of students have experienced cybervictimization',               btnText: 'What About Your Students?',              color: '#FF6900', Icon: SecurityOutlinedIcon },
    //     { value: '26%', label: 'of students have met online strangers offline',                  btnText: 'Create a 45-Minute Lesson',              color: '#EEE921', Icon: PeopleOutlineIcon },
    //     { value: '20%', label: 'of students use AI to cheat on homework',                        btnText: 'Teach Them How to Use AI Ethically',     color: '#3CC6F3', Icon: ReportProblemOutlinedIcon },
    //     { value: '54%', label: 'of students do not use AI tools ethically or responsibly',       btnText: 'Create a 3-Session Program',             color: '#6A8EC9', Icon: ChatBubbleOutlineIcon },
    //     { value: '76%', label: 'of students have experienced at least 1 cyber incident',         btnText: 'Equip Them with Cyber Citizenship Skills', color: '#EA4E99', Icon: LockOutlinedIcon },
    //     { value: '92%', label: 'of students have used generative AI tools for school work',      btnText: 'Do Yours?',                              color: '#B7D334', Icon: EmojiObjectsOutlinedIcon },
    // ];

    /*
     * Ideas narrow to the chosen category; with none chosen they all show.
     * Never filtered by audience — a cyberbullying idea written for ages 9-12
     * is still a useful prompt for a teacher who picked High School, and
     * hiding it would leave the row half empty for no reason the reader can
     * see.
     */
    /*
     * All four ideas, always — the row is four wide on the slide and a
     * filtered row left one card beside three empty columns. Choosing a
     * category brings its idea to the FRONT and marks it, so the link is
     * still visible without emptying the row.
     */
    const visibleIdeas = categoryId
        ? [...IDEA_CARDS].sort((a, b) => (b.category === categoryId) - (a.category === categoryId))
        : IDEA_CARDS;

    const handleChipClick = (text) => {
        setInputValue(text);
        // Optional: auto-submit or just prefill
    };

    // const handleStatClick = (text) => {
    //     history.push('/signup', { userMessage: text });
    // };

    return (
        <Box className={classes.pagePage} style={isDark ? { backgroundImage: 'none', backgroundColor: '#0a0a0a' } : undefined}>
            {/*
              * Step-0 header: logo CENTRED, My Workspace and the profile icon on
              * the right.
              *
              * Deliberately not the public page bar. Packages and the theme
              * toggle belong on the anonymous front door; this page is only ever
              * reached by someone already signed in, who needs a way back to
              * their workspace instead.
              */}
            {/* Stays at the top while the page scrolls (LA-LC-05Oct-1). */}
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2, position: 'sticky', top: 0, zIndex: 1100, backgroundColor: 'var(--brand-surface, #fff)' }}>
                <img src={PoweredByDQ_Logo} alt="Logo" className={classes.logo} />
                <Box sx={{ position: 'absolute', right: 16, display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Button
                        onClick={() => history.push('/my_workspace')}
                        variant="contained"
                        size="small"
                        sx={{
                            textTransform: 'none',
                            fontWeight: '700 !important',
                            fontSize: '15px !important',
                            borderRadius: '999px !important',
                            backgroundColor: '#1B9AAA !important',
                            color: '#fff !important',
                            px: '20px !important',
                            py: '7px !important',
                            boxShadow: 'none !important',
                            '&:hover': { backgroundColor: '#178795 !important' },
                        }}
                    >
                        My Workspace
                    </Button>
                    <AccountMenu style={{ zIndex: 1200 }} />
                </Box>
            </Box>

            {/* Hero Section */}
            <Box className={classes.hero}>
                <Box className={classes.card}>
                    <Box className={classes.heroPill}>
                        <span className={classes.heroPillIcon}>✦</span>
                        AI-Powered Curriculum Generator
                    </Box>
                    <Typography variant="h1" className={classes.title}>
                        Create and assess AI–digital literacy programs, instantly.
                    </Typography>
                    <Typography variant="h2" className={classes.titleSub}>
                        Aligned with global standards.
                    </Typography>
                    <Typography className={classes.subtitle}>
                        Tell us what you want your learners to achieve. LearnCreator will design
                        the program, recommend the right frameworks, and help you measure whether
                        it worked.
                    </Typography>
                    <Typography className={classes.lcFieldLabel}>
                        What do you want your learners to build?
                    </Typography>

                    <Box className={classes.inputRow}>
                        <Box className={classes.inputContainer}>
                            <textarea
                                className={classes.textArea}
                                placeholder={isFocused || inputValue ? "" : "For example: I want my Grade 5 students to understand how AI works and critically evaluate what it tells them."}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => setIsFocused(false)}
                            />
                            <Button className={classes.createBtn} onClick={handleCreateProgram} disableElevation>
                                Create program
                            </Button>
                        </Box>
                        <Box className={classes.frameworksPanel}>
                            <Typography className={classes.frameworksTitle}>
                                100+ international and national<br/>frameworks such as:
                            </Typography>
                            {/*
                              * Still the rolling list: the panel beside the input is the
                              * teaser. The named, numbered five the deck asks for live in
                              * their own section further down, where there is room to say
                              * what alignment actually means.
                              */}
                            {fwItems.map((item) => (
                                <Box key={item.id} className={item.exiting ? classes.frameworkPillExiting : classes.frameworkPill}>
                                    {item.text}
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    <Typography className={classes.lcNoExpertise}>
                        No curriculum expertise required. Your first draft takes about two minutes.
                    </Typography>

                    {/*
                      * Slide 4, continuing straight on from the hero — Ian: "the content
                      * from 'Need inspiration?' needs to be continued" from the previous
                      * screenshot. One page, no break.
                      */}
                    <Typography className={classes.helpText}>
                        <EmojiObjectsOutlinedIcon className={classes.helpTextIcon} />
                        Need inspiration? Choose an audience and explore an idea.
                    </Typography>

                    <Typography className={classes.lcSectionLabel}>Who are your learners?</Typography>
                    <Box className={classes.lcAudienceRow}>
                        {AUDIENCES.map((a) => (
                            <Box
                                key={a.code}
                                className={a.code === audienceCode ? classes.lcAudChipOn : classes.lcAudChip}
                                title={`Ages ${a.ages}`}
                                onClick={() => {
                                    const next = a.code === audienceCode ? '' : a.code;
                                    setAudienceCode(next);
                                    setInputValue((v) => withAudience(v, next));
                                }}
                            >
                                {a.label}
                            </Box>
                        ))}
                    </Box>

                    {/*
                      * No confirmation line here. The chosen chip turns solid, which
                      * is the whole signal the deck gives; a sentence restating it
                      * was invented, not approved, and read poorly for the bands
                      * whose range is a phrase ("General Public, ages all ages").
                      * The code still travels to the builder either way.
                      */}
                    <Typography className={classes.lcSectionLabel}>What would you like to build?</Typography>
                    <Box className={classes.lcCatRow}>
                        {BUILD_CATEGORIES.map((c) => (
                            <Box
                                key={c.id}
                                className={c.id === categoryId ? classes.lcCatCardOn : classes.lcCatCard}
                                onClick={() => setCategoryId(c.id === categoryId ? '' : c.id)}
                            >
                                <span className={classes.lcCatNum}>{c.n}</span>
                                <span className={classes.lcCatLabel}>{c.label}</span>
                            </Box>
                        ))}
                    </Box>

                    <Typography className={classes.lcSectionLabel}>Ideas from across the platform</Typography>
                    <Box className={classes.lcIdeaRow}>
                        {visibleIdeas.map((idea) => (
                            <Box
                                key={idea.prompt}
                                // No highlighted card: every idea has the same white background (7 Oct 2026).
                                className={classes.lcIdeaCard}
                                onClick={() => {
                                    // The card states an audience; taking it saves the person
                                    // answering that question twice. It is written into the box
                                    // with the idea, like a chosen chip.
                                    const aud = audienceCode || idea.audience;
                                    if (!audienceCode) setAudienceCode(idea.audience);
                                    handleChipClick(withAudience(idea.prompt, aud));
                                }}
                            >
                                <span className={classes.lcIdeaText}>{idea.prompt}</span>
                                <span className={classes.lcIdeaMeta}>{idea.meta}</span>
                            </Box>
                        ))}
                    </Box>

                    <Box className={classes.lcAlignBlock}>
                        <Box>
                            <Typography className={classes.lcAlignTitle}>
                                Automatically aligned to relevant frameworks
                            </Typography>
                            <Typography className={classes.lcAlignBody}>
                                LearnCreator recommends the most relevant frameworks based on your
                                learners, location, learning goals and delivery context. You can
                                review or change them before publishing.
                            </Typography>
                        </Box>
                        {/* Scrollable, per the slide note — the list must not push the
                            page down as it grows. */}
                        <Box className={classes.lcAlignList}>
                            {NAMED_FRAMEWORKS.map((f) => (
                                <Box key={f.n} className={classes.lcAlignRow}>
                                    <span className={classes.lcAlignNum}>{f.n}</span>
                                    <span>{f.label}</span>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Did You Know Section — commented out (Approved Fixes); uncomment to restore.
            <Box className={classes.didYouKnowSection}>
                <Typography className={classes.didYouKnowTitle}>
                    Did You Know?
                </Typography>
                <Box className={classes.statsRow}>
                    {stats.map((stat, idx) => (
                        <Box key={idx} className={classes.statCard}>
                            <stat.Icon style={{ color: stat.color, fontSize: 26 }} />
                            <Typography className={classes.statValue} style={{ color: stat.color }}>
                                {stat.value}
                            </Typography>
                            <Typography className={classes.statLabel}>
                                {stat.label}
                            </Typography>
                            <Button
                                className={classes.statBtn}
                                style={{ backgroundColor: stat.color, color: '#000000' }}
                                onClick={() => handleStatClick(stat.btnText)}
                            >
                                {stat.btnText}
                            </Button>
                        </Box>
                    ))}
                </Box>
            </Box>
            */}

            {/*
              * The page ENDS at the frameworks list.
              *
              * Slide 4 is the whole spec for step 0, and it stops there — the
              * "From Idea to Measurable Impact" three-step section that follows
              * on the public page is not part of it. That section sells the idea
              * to a visitor who has not committed; someone who reached this page
              * from Solutions has already chosen, and it only pushes the input
              * they came here to use further up the screen.
              *
              * It stays on the public page, which is untouched.
              */}
            <UnlockCapabilityDialog
                open={unlockOpen}
                onClose={() => setUnlockOpen(false)}
                authUser={authUser}
            />
        </Box>
    );
};

export default DQProV2StepZero;
